import {
  RemoteFiltersChip,
  RemoteFiltersConfiguration,
  RemoteFiltersFieldType,
} from 'frontcore/presentation/remote-filters/domain/types'
import {
  remoteFiltersURLBooleanParser,
  remoteFiltersURLDateParser,
  remoteFiltersURLDateRangeParser,
  remoteFiltersURLOptionListParser,
  remoteFiltersURLOptionParser,
} from 'frontcore/presentation/remote-filters/domain/remote-filters-parsers'

export abstract class RemoteFiltersValue {

  static buildDefault (configuration: RemoteFiltersConfiguration) {
    switch (configuration.filterType) {
    case RemoteFiltersFieldType.SELECT_SINGLE:
    case RemoteFiltersFieldType.SELECT_SINGLE_REMOTE:
    case RemoteFiltersFieldType.SEARCH:
      return new RemoteFiltersOptionValue(configuration, configuration.defaultOption)
    case RemoteFiltersFieldType.SELECT_MULTIPLE:
    case RemoteFiltersFieldType.SELECT_MULTIPLE_REMOTE:
      return new RemoteFiltersOptionListValue(configuration, configuration.defaultOption)
    case RemoteFiltersFieldType.SWITCH:
      return new RemoteFiltersBooleanValue(configuration, configuration.defaultOption)
    case RemoteFiltersFieldType.DATE:
      return new RemoteFiltersDateValue(configuration, configuration.defaultOption)
    case RemoteFiltersFieldType.DATE_RANGE:
      return new RemoteFiltersDateRangeValue(configuration, configuration.defaultOption)
    case RemoteFiltersFieldType.SEGMENTED_BUTTON:
      return new RemoteFiltersSegmentedButtonItemValue(configuration, configuration.defaultOption)
    }
  }

  static buildFromURL (
    configuration: RemoteFiltersConfiguration,
    value: string
  ): RemoteFiltersValue {
    switch (configuration.filterType) {
    case RemoteFiltersFieldType.SELECT_SINGLE:
    case RemoteFiltersFieldType.SELECT_SINGLE_REMOTE:
    case RemoteFiltersFieldType.SEARCH:
      return new RemoteFiltersOptionValue(configuration, remoteFiltersURLOptionParser(value))
    case RemoteFiltersFieldType.SELECT_MULTIPLE:
    case RemoteFiltersFieldType.SELECT_MULTIPLE_REMOTE:
      return new RemoteFiltersOptionListValue(configuration, remoteFiltersURLOptionListParser(value))
    case RemoteFiltersFieldType.SWITCH:
      return new RemoteFiltersBooleanValue(configuration, remoteFiltersURLBooleanParser(value))
    case RemoteFiltersFieldType.DATE:
      return new RemoteFiltersDateValue(configuration, remoteFiltersURLDateParser(value))
    case RemoteFiltersFieldType.DATE_RANGE:
      return new RemoteFiltersDateRangeValue(configuration, remoteFiltersURLDateRangeParser(value))
    case RemoteFiltersFieldType.SEGMENTED_BUTTON:
      return new RemoteFiltersSegmentedButtonItemValue(configuration, remoteFiltersURLOptionParser(value))
    }
  }

  abstract value?: number | string | (number | string)[] | boolean | Date | Date[] | null
  abstract get chips (): RemoteFiltersChip[]
  abstract get queryParameters (): Record<string, string> | undefined

  readonly configuration: RemoteFiltersConfiguration


  protected constructor (configuration: RemoteFiltersConfiguration) {
    this.configuration = configuration
  }

  abstract clone (): RemoteFiltersValue

}

type ValidOptions = RemoteFiltersConfiguration['defaultOption'] | null

export class RemoteFiltersOptionValue extends RemoteFiltersValue {

  value?: number | string | null

  static isValidValue (value: ValidOptions): value is RemoteFiltersOptionValue['value'] {
    return value == null
      || typeof value === 'number'
      || typeof value === 'string'
  }

  constructor (configuration: RemoteFiltersConfiguration, value: ValidOptions) {
    super(configuration)
    if (!RemoteFiltersOptionValue.isValidValue(value)) {
      throw new Error(`Unrecognized type for initial value of ${configuration.filterId}`)
    }
    this.value = value
  }

  get chips (): RemoteFiltersChip[] {
    const text = this.configuration.options.find((cfg) => cfg.id === this.value)?.text
    if (this.value === undefined || this.value === null || text === undefined) { return [] }
    return [{
      filterId: this.configuration.filterId,
      filterType: RemoteFiltersFieldType.SELECT_SINGLE,
      id: this.value,
      required: this.configuration.required,
      text,
    }]
  }

  get queryParameters (): Record<string, string> | undefined {
    const parsedValue = this.value?.toString()
    if (parsedValue === undefined || parsedValue.length === 0) { return undefined }
    return { [this.configuration.filterMap]: parsedValue }
  }

  clone (): RemoteFiltersValue {
    return new RemoteFiltersOptionValue(this.configuration, this.value)
  }

}

export class RemoteFiltersOptionListValue extends RemoteFiltersValue {

  static isValidValue (value: ValidOptions): value is RemoteFiltersOptionListValue['value'] {
    if (value == null) {
      return true
    }
    return Array.isArray(value) && value.every(RemoteFiltersOptionValue.isValidValue)
  }

  value: number[] | string[]

  constructor (configuration: RemoteFiltersConfiguration, value: ValidOptions) {
    super(configuration)
    if (!RemoteFiltersOptionListValue.isValidValue(value)) {
      throw new Error(`Unrecognized type for initial value of ${configuration.filterId}`)
    }
    this.value = value ?? []
  }

  get chips (): RemoteFiltersChip[] {
    const chips: RemoteFiltersChip[] = []
    for (const value of this.value) {
      const text = this.configuration.options.find((cfg) => cfg.id === value)?.text
      if (text === undefined) { continue }
      chips.push({
        filterId: this.configuration.filterId,
        filterType: RemoteFiltersFieldType.SELECT_MULTIPLE,
        id: value,
        required: this.configuration.required,
        text,
      })
    }
    return chips
  }

  get queryParameters (): Record<string, string> | undefined {
    const parsedValue = this.value?.join(',')
    if (parsedValue === undefined || parsedValue.length === 0) { return undefined }
    return { [this.configuration.filterMap]: parsedValue }
  }

  clone (): RemoteFiltersValue {
    return new RemoteFiltersOptionListValue(this.configuration, this.value)
  }

}

export class RemoteFiltersBooleanValue extends RemoteFiltersValue {

  static isValidValue (value: ValidOptions): value is RemoteFiltersBooleanValue['value'] {
    return value == null || typeof value === 'boolean'
  }

  value?: boolean | null

  constructor (configuration: RemoteFiltersConfiguration, value: ValidOptions) {
    super(configuration)
    if (!RemoteFiltersBooleanValue.isValidValue(value)) {
      throw new Error(`Unrecognized type for initial value of ${configuration.filterId}`)
    }
    this.value = value
  }

  get chips (): RemoteFiltersChip[] {
    if (!this.value) { return [] }
    return [{
      filterId: this.configuration.filterId,
      filterType: RemoteFiltersFieldType.SWITCH,
      id: this.configuration.filterId,
      required: this.configuration.required,
      text: this.configuration.label,
    }]
  }

  get queryParameters (): Record<string, string> | undefined {
    const parsedValue = this.value?.toString()
    if (parsedValue === undefined) { return parsedValue }
    return { [this.configuration.filterMap]: parsedValue }
  }

  clone (): RemoteFiltersValue {
    return new RemoteFiltersBooleanValue(this.configuration, this.value)
  }

}

export class RemoteFiltersDateValue extends RemoteFiltersValue {

  static isValidValue (value: ValidOptions): value is RemoteFiltersDateValue['value'] {
    return value == null || (value instanceof Date && !isNaN(value.getTime()))
  }

  value?: Date | null

  constructor (configuration: RemoteFiltersConfiguration, value: ValidOptions) {
    super(configuration)
    if (!RemoteFiltersDateValue.isValidValue(value)) {
      throw new Error(`Unrecognized type for initial value of ${configuration.filterId}`)
    }
    this.value = value
  }

  get chips (): RemoteFiltersChip[] {
    if (!this.value) { return [] }
    return [{
      filterId: this.configuration.filterId,
      filterType: RemoteFiltersFieldType.DATE,
      id: this.configuration.filterId,
      required: this.configuration.required,
      text: this.value.toLocaleDateString(),
    }]
  }

  get queryParameters (): Record<string, string> | undefined {
    const parsedValue = this.value?.toISOString()
    if (parsedValue === undefined) { return parsedValue }
    return { [this.configuration.filterMap]: parsedValue }
  }

  clone (): RemoteFiltersValue {
    return new RemoteFiltersDateValue(this.configuration, this.value)
  }

}

export class RemoteFiltersDateRangeValue extends RemoteFiltersValue {

  static isValidValue (value: ValidOptions): value is RemoteFiltersDateRangeValue['value'] {
    if (value == null) {
      return true
    }
    return Array.isArray(value) && value.every(RemoteFiltersDateValue.isValidValue)
  }

  value: Date[]

  constructor (configuration: RemoteFiltersConfiguration, value: ValidOptions) {
    super(configuration)
    if (!RemoteFiltersDateRangeValue.isValidValue(value)) {
      throw new Error(`Unrecognized type for initial value of ${configuration.filterId}`)
    }
    this.value = value.map(option => new Date(option)) ?? []
  }

  get chips (): RemoteFiltersChip[] {
    if (this.value.length !== 2) { return [] }
    const text = this.value.map((date) => date.toLocaleDateString()).join(' - ')
    return [{
      filterId: this.configuration.filterId,
      filterType: RemoteFiltersFieldType.DATE_RANGE,
      id: this.configuration.filterId,
      required: this.configuration.required,
      text,
    }]
  }

  get queryParameters (): Record<string, string> | undefined {
    const before = this.value.at(1)
    const after = this.value.at(0)
    if (after === undefined || before === undefined) { return undefined }
    return {
      [`${this.configuration.filterMap}_after`]: after.toISOString(),
      [`${this.configuration.filterMap}_before`]: before.toISOString(),
    }
  }

  clone (): RemoteFiltersDateRangeValue {
    return new RemoteFiltersDateRangeValue(this.configuration, this.value)
  }

}

export class RemoteFiltersSegmentedButtonItemValue extends RemoteFiltersValue {

  value: number

  static isValidValue (value: ValidOptions): value is RemoteFiltersSegmentedButtonItemValue['value'] {
    return typeof value === 'number'
  }

  constructor (configuration: RemoteFiltersConfiguration, value: ValidOptions) {
    super(configuration)
    if (!RemoteFiltersSegmentedButtonItemValue.isValidValue(value)) {
      throw new Error(`Unrecognized type for initial value of ${configuration.filterId}`)
    }
    this.value = value
  }

  get chips (): RemoteFiltersChip[] {
    const text = this.configuration.options.find((cfg) => cfg.id === this.value)?.text
    if (text === undefined) { return [] }
    return [{
      filterId: this.configuration.filterId,
      filterType: RemoteFiltersFieldType.SEGMENTED_BUTTON,
      id: this.value,
      required: this.configuration.required,
      text,
    }]
  }

  get queryParameters (): Record<string, string> | undefined {
    return { [this.configuration.filterMap]: this.value.toString() }
  }

  clone (): RemoteFiltersValue {
    return new RemoteFiltersSegmentedButtonItemValue(this.configuration, this.value)
  }

}
